
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { City } from "@/types/city";

export const useCities = (countryId?: string | null) => {
  return useQuery({
    queryKey: ["cities", countryId || "all"],
    queryFn: async () => {
      let query = supabase
        .from("cities")
        .select("*");
      
      if (countryId) {
        query = query.eq('country_id', countryId);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });
      
      if (error) throw error;
      return data as City[];
    },
  });
};

export const useCreateCity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (cityData: Omit<City, "id" | "created_at" | "updated_at">) => {
      const { data, error } = await supabase
        .from("cities")
        .insert([cityData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
  });
};

export const useUpdateCity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...cityData }: Partial<City> & { id: string }) => {
      const { data, error } = await supabase
        .from("cities")
        .update({ ...cityData, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
  });
};

export const useDeleteCity = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("cities")
        .delete()
        .eq("id", id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cities"] });
    },
  });
};
